import classNames from "classnames";
import DOMPurify from "dompurify";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  type FormEvent,
  type RefObject
} from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType
} from "../../../../Shared/Buttons/React";
import { Modal } from "../../../../Shared/Modal/React";
import { useProvetStatic, useTranslationContext } from "../../context";
import { ProvetFormData } from "../../types";
import { ApiError, isUnconfirmedBookingError } from "../../utilities/apiClient";
import {
  formatAppointmentDate,
  formatAppointmentTime
} from "../../utilities/dateTimeFormatters";
import { ErrorMessage } from "../shared/errorMessage";

interface ProvetSummaryModalProps {
  active: boolean;
  formData?: ProvetFormData;
  isSubmitting: boolean;
  submissionError?: Error | null;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  onClose?: () => void;
  onRetry?: () => void;
  onTimeslotConflict?: () => void;
  onPhoneVerificationExpired?: () => Promise<void>;
  scrollIndicatorRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  modalRef?: RefObject<HTMLDivElement>;
}

/**
 * Modal that displays a summary of the booking before final confirmation.
 * Shows all selected information including clinic, treatment, time, customer, and patient details.
 */
export const ProvetSummaryModal = ({
  active,
  formData,
  isSubmitting,
  submissionError,
  onSubmit,
  onCancel,
  onClose,
  onRetry,
  onTimeslotConflict,
  onPhoneVerificationExpired,
  scrollIndicatorRef,
  contentRef,
  modalRef
}: ProvetSummaryModalProps) => {
  const translationContext = useTranslationContext();
  // The site language, so that the date is not shown in the browser's or default language
  const { language } = useProvetStatic();
  const translations = translationContext?.translations;
  const titleId = "provet-summary-modal-title";

  // A booking that timed out may still have been created, so it gets its own message without
  // "Try again", to avoid a double booking
  const isUnconfirmed = isUnconfirmedBookingError(submissionError);

  // Every rejection (bot checks, CSRF) and every server fault is the same 500, so the customer
  // gets one message with a way forward, which reveals nothing about what was detected
  const displayedError = useMemo(() => {
    const isRejected =
      submissionError instanceof ApiError && submissionError.statusCode === 500;

    if (!isUnconfirmed && !isRejected) {
      return submissionError;
    }

    const clinic = formData?.selectedDepartment?.name;
    const phone = formData?.selectedDepartment?.address_info?.phone;

    if (!clinic) {
      return new Error(
        translations?.errorServerError ||
          "A server error occurred. Please try again later."
      );
    }

    const template = isUnconfirmed
      ? translations?.errorBookingNotConfirmed ||
        "We couldn't confirm your booking. Check your email or call {clinic} before trying again."
      : phone
        ? translations?.errorBookingRejected ||
          "We couldn't complete your booking online. Please try again later or call {clinic} on {phone}."
        : translations?.errorBookingRejectedNoPhone ||
          "We couldn't complete your booking online. Please try again later or contact {clinic}.";

    const values: Record<string, string> = { clinic, phone: phone ?? "" };

    return new Error(
      template.replace(/\{(clinic|phone)\}/g, (_, key: string) => values[key])
    );
  }, [submissionError, isUnconfirmed, formData?.selectedDepartment, translations]);

  // Esc and click outside do nothing while the booking is sent, or when it may already exist.
  // The modal only replaces its Esc listener after paint, so an Esc right after Confirm could
  // still run the previous handler; reading the current one from a ref set at commit avoids that.
  const closeRef = useRef<(() => void) | undefined>();
  useLayoutEffect(() => {
    closeRef.current = isSubmitting || isUnconfirmed ? undefined : onClose || onCancel;
  });
  const handleClose = useCallback(() => closeRef.current?.(), []);

  return (
    <Modal
      active={active}
      onClose={handleClose}
      hasFooter={true}
      ref={modalRef}
      titleId={titleId}
      modalName="summary"
    >
      {/* Modal Header and Content */}
      <div
        ref={contentRef}
        data-modal-scrollable-content
        className="flex flex-col gap-2xl w-full p-xs text-t-neutral-800 md:w-[51rem] md:p-lg pb-0"
      >
        {/* Error Message */}
        {displayedError && (
          <ErrorMessage
            error={displayedError}
            onRetry={isUnconfirmed ? undefined : onRetry}
            onTimeslotConflict={onTimeslotConflict}
            onPhoneVerificationExpired={onPhoneVerificationExpired}
          />
        )}

        {/* Header */}
        <div className="flex flex-col gap-md">
          <h2
            id={titleId}
            className="flex items-center font-guardian text-dark-blue text-h3 font-t-semi-bold leading-lg sm:leading-xxl gap-xs md:gap-sm"
          >
            <Svg href="/dist/sprite.svg#sys-info" additionalClass="fill-t-neutral-700" />
            {translations?.summary}
          </h2>
          <span className="text-lg text-t-neutral-800">
            {translations?.summaryInformation}
          </span>
        </div>

        {/* Booking Details */}
        <div
          className={classNames(
            "flex flex-col gap-lg",
            "text-t-md font-normal leading-lg text-t-neutral-800 md:text-t-md md:leading-xxl",
            "[&_a]:font-t-bold [&_a]:underline [&_a]:decoration-turquoise-800 [&_a]:underline-offset-[0.35rem]",
            "[&_p]:mb-md [&_p]:last:mb-0 [&_ul]:mb-md [&_ul]:last:mb-0 [&_ol]:mb-md [&_ol]:last:mb-0",
            "[&_ul]:list-disc [&_ol]:list-decimal [&_ul_li::before]:bg-dark-blue [&_ol_li::before]:text-dark-blue [&_ol_li::before]:bg-white"
          )}
        >
          {/* Location */}
          <div className="flex flex-col gap-xs text-t-neutral-800">
            <h3 className="text-distinct-sm font-t-semi-bold uppercase leading-[1px]">
              {translations?.location}
            </h3>
            <div className="flex">
              <span>{formData?.selectedDepartment?.name}</span>
            </div>
          </div>
          {/* Treatment */}
          <div className="flex flex-col gap-xs text-t-neutral-800">
            <h3 className="text-distinct-sm font-t-semi-bold uppercase leading-[1px]">
              {translations?.treatment}
            </h3>
            <span>{formData?.selectedReason?.name}</span>
          </div>
          {/* Time */}
          <div className="flex flex-col gap-xs text-t-neutral-800">
            <h3 className="text-distinct-sm font-t-semi-bold uppercase leading-[1px]">
              {translations?.time}
            </h3>
            <span className="inline-flex items-center gap-sm">
              {formData?.selectedAppointmentTime?.availableTime && (
                <>
                  {formatAppointmentDate(
                    formData.selectedAppointmentTime.availableTime,
                    language
                  )}
                  <span className="inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                    {" "}
                  </span>
                  {formatAppointmentTime(formData.selectedAppointmentTime.availableTime)}
                  {formData.selectedAppointmentTime.selectedVeterinarianName && (
                    <>
                      <span className="inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                        {" "}
                      </span>
                      {formData.selectedAppointmentTime.selectedVeterinarianName}
                    </>
                  )}
                </>
              )}
            </span>
          </div>
          {/* Customer Information */}
          <div className="flex flex-col gap-xs text-t-neutral-800">
            <h3 className="text-distinct-sm font-t-semi-bold uppercase leading-[1px]">
              {translations?.customerInformation}
            </h3>
            <span className="flex-col sm:flex-row inline-flex sm:items-center gap-xs sm:gap-sm">
              <span>
                {formData?.contactDetails?.client.firstName}{" "}
                {formData?.contactDetails?.client.lastName}
              </span>
              <span className="hidden sm:inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                {" "}
              </span>
              <span>{formData?.contactDetails?.client.email}</span>
              <span className="hidden sm:inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                {" "}
              </span>
              <span>{formData?.contactDetails?.client.phoneNumber}</span>
              {(formData?.contactDetails?.client.zipCode ||
                formData?.contactDetails?.client.streetAddress) && (
                <>
                  <span className="hidden sm:inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                    {" "}
                  </span>
                  <span>{formData?.contactDetails?.client.zipCode}</span>
                  <span>{formData?.contactDetails?.client.streetAddress}</span>
                </>
              )}
            </span>
          </div>
          {/* Patient Information */}
          <div className="flex flex-col gap-xs text-t-neutral-800">
            <h3 className="text-distinct-sm font-t-semi-bold uppercase leading-[1px]">
              {translations?.patientInformation}
            </h3>
            <span className="inline-flex items-center gap-sm">
              {formData?.contactDetails?.patient.name}
              <span className="inline-flex w-xxs h-xxs mx-xxs bg-turquoise-600 rounded-full">
                {" "}
              </span>
              {formData?.contactDetails?.patient.species?.label}{" "}
              {formData?.contactDetails?.patient.breed?.label &&
                `(${formData.contactDetails.patient.breed.label})`}
            </span>
            {formData?.contactDetails?.patient.notes && (
              <div className="p-sm mt-xs mb-lg border border-neutral-200 rounded-tl-br-sm text-t-neutral-600">
                {formData.contactDetails.patient.notes}
              </div>
            )}
          </div>
          {/* Treatment Instructions (if available) */}
          {formData?.selectedReason?.instructions && (
            <div className="flex flex-col gap-xs p-md mb-lg border border-neutral-200 rounded-tl-br-sm bg-neutral-50 text-t-neutral-600">
              <h3 className="text-distinct-sm lg:text-distinct-md text-t-neutral-800 uppercase">
                {translations?.noteFromClinic}
              </h3>
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    formData.selectedReason.instructions.replace(/\n/g, "<br />")
                  )
                }}
              />
            </div>
          )}
        </div>
      </div>
      {/* Footer with Scroll Indicator and Action Buttons */}
      <div className="flex flex-col sticky z-[2] justify-center bottom-0">
        {/* Scroll Indicator */}
        <div
          ref={scrollIndicatorRef}
          data-scroll-indicator
          className="flex justify-center items-center h-[6.25rem] w-full bg-gradient-to-t from-white to-transparent via-white/90 transition-all duration-300 ease-in-out opacity-100"
        >
          <div className="flex h-[3.125rem] w-[3.125rem] justify-center items-center border-2 border-dark-blue rounded-full overflow-hidden">
            <Svg
              href="/svg/system/arrow-down.svg"
              title={translations?.scrollDown}
              size={SvgSize.LG}
              additionalClass={"fill-dark-blue animate-bounce"}
            />
          </div>
        </div>
        {/* Action Buttons */}
        <div className="py-lg px-0 bg-white border-t border-neutral-200">
          <div className="flex flex-col gap-md sm:flex-row">
            <Button
              type={ButtonType.Submit}
              size={ButtonSize.LG}
              color={ButtonColor.Green}
              disabled={isSubmitting || !!submissionError}
              isLoading={isSubmitting}
              onClick={onSubmit}
              data-online-booking-action="confirm"
            >
              {translations?.confirmBooking}
            </Button>
            <Button
              type={ButtonType.Button}
              size={ButtonSize.LG}
              color={ButtonColor.OutlineNeutral}
              onClick={onCancel}
              disabled={isSubmitting}
              data-online-booking-action="cancel"
            >
              {translations?.change}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
